import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Copy, Loading, StringHelpers, SystemText } from '@salesperformanceint/e4shared';
import Chart from './chart';
import { apiGet } from 'services';
import { getMetricFromPickerDetails } from 'helpers/metrics';
import { getQuarterIndex } from 'helpers/company';
import { e4Format, e4Formats, addDays, isAfter, isBefore } from 'helpers/dates';
import { setDashboardCollection } from 'actions/dashboard';

export const MetricGraph = ({ task }) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);
    const allMetrics = useSelector(state => state.dashboard.metrics);

    const st = (path, ...rest) => SystemText(`Objective/MetricGraph/${path}`, ...rest);

    const text = {
        loading: st('Loading'),
        quarter: st('Quarter'),
        month: st('Month'),
        week: st('Week'),
    };

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [keys, setKeys] = React.useState([]);
    const [period, setPeriod] = React.useState('M');
    const [periodName, setPeriodName] = React.useState('Month');
    const [metric, setMetric] = React.useState('');
    const [goals, setGoals] = React.useState([]);
    const [goal, setGoal] = React.useState({});
    const [target, setTarget] = React.useState({});
    const [targetText, setTargetText] = React.useState(text.loading);

    const loadAllMetrics = () => {
        apiGet('all-fields', user?.guid, response => dispatch(setDashboardCollection('metrics', response)));
    };

    React.useEffect(() => {
        apiGet('all-quotas', user.guid, setGoals);
    }, []);

    React.useEffect(() => {
        if (!allMetrics?.length) {
            loadAllMetrics();
        }
    }, [allMetrics]);

    React.useEffect(() => {
        if (task?.metric_Details) {
            setMetric(getMetricFromPickerDetails(allMetrics, task.metric_Details, {}));
        } else {
            setMetric({});
        }
    }, [allMetrics, task?.metric_Details]);

    React.useEffect(() => {
        if (task?.metric_Target) {
            setTarget(JSON.parse(task.metric_Target));
        } else {
            setTarget({});
        }
    }, [task?.metric_Target]);

    React.useEffect(() => {
        if (goals?.length && target?.goal) {
            setGoal(goals.find(x => x.id === target.goal));
        } else {
            setGoal({});
        }
    }, [goals, target?.goal]);

    React.useEffect(() => {
        if (goal) {
            setPeriod(goal.period);
            setPeriodName(SystemText(`General/Controls/PeriodPicker/Period/${goal.period}`));
        }
    }, [goal]);

    // Set target text
    React.useEffect(() => {
        if (target && metric?.id) {
            let prefix = '';
            let value = 0;

            if (metric?.format === 'M' && target?.range !== 'P') {
                prefix = user.currencySymbol;
            }

            value = target.value?.toLocaleString('en');
            let duration = target.duration || 1;

            if (target.range === 'P' && target?.period === 'all') {
                setTargetText(st('PercentPeriodAll', prefix, value));
            } else if (target.range === 'P') {
                setTargetText(
                    st(
                        'PercentPeriodSome',
                        prefix,
                        value,
                        goal?.name?.replace('#', '&#35;'),
                        duration,
                        periodName.toLowerCase()
                    )
                );
            } else if (target?.period === 'all') {
                setTargetText(st('TotalPeriodAll', prefix, value));
            } else {
                setTargetText(st('TotalPeriodSome', prefix, value, target.period, duration));
            }
        }
    }, [target, metric, user, periodName]);

    React.useEffect(() => {
        if (task && period) {
            const taskDetails = JSON.parse(task.metric_Details || '{}');
            const taskTarget = JSON.parse(task.metric_Target || '{}');

            if (!taskTarget.period) {
                taskTarget.period = period === 'W' ? 'week' : period === 'Q' ? 'quarter' : 'month';
            }

            const dateFormat = () => (period === 'W' ? e4Formats.week : e4Formats.monthShort);

            const monthlyQuota = (d, q) => {
                if (!q) return 0;
                const fy = user.fy_Start_Date ? new Date(user.fy_Start_Date) : new Date(2000, 1, 1);
                const quarter = getQuarterIndex(fy, new Date(d));
                const values = q.values.filter(v => new Date(v.valid_For < new Date(d))).sort((a, b) => (a > b ? -1 : 1));
                const value = values.find(f => f);
                if (!value) return 0;
                return value.value || value.values[quarter] / (taskTarget.period === text.month ? 3 : 1);
            };

            const range =
                period === 'all'
                    ? period === 'W'
                        ? 'week'
                        : period === 'Q'
                          ? 'quarter'
                          : 'month'
                    : periodName.toLowerCase();

            const url =
                taskDetails.type === 'CRM'
                    ? `crm-over-time/${task.profile_Id}/${taskDetails.metricId}/${task.id}?range=${range}`
                    : `integration-overtime/${task.profile_Id}/${taskDetails.metricId}/${task.id}?range=${range}`;

            apiGet(url, user.guid, async response => {
                const temp = response.map(r => ({
                    Actual: Number(r.value),
                    Target: taskTarget.value,
                    Goal: null,
                    date: r.date_Set,
                    id: e4Format(r.date_Set, dateFormat()),
                }));

                if (taskTarget.range === 'P') {
                    await apiGet(`quota-values/${task.profile_Id}`, user.guid, response => {
                        const quota = response.find(x => x.id === goal.id);
                        temp.forEach(t => {
                            t.Target = Math.round((monthlyQuota(t.date, quota) * taskTarget.value) / 100, 0);
                        });
                    });
                }

                task.date_Due = task.due;

                setData(
                    temp
                        .map(t => {
                            const after = isAfter(t.date, task.date_Due);
                            const before = isBefore(t.date, task.date_Assigned);
                            if (!after && !before) return t;

                            // if before, get number after this but before assigned. allow if less than 5
                            if (before) {
                                const befores = temp.filter(
                                    x => isAfter(x.date, t.date) && isBefore(x.date, task.date_Assigned)
                                );
                                if (befores.length < 5) {
                                    return t;
                                }
                            }

                            // if after, get number before this but after due. allow if less than 5
                            if (after) {
                                const afters = temp.filter(x => isBefore(x.date, t.date) && isAfter(x.date, task.date_Due));
                                if (afters.length < 5) {
                                    return t;
                                }
                            }

                            return null;
                        })
                        .filter(t => t)
                );
            });

            setKeys([st('Actual'), st('Target')]);
            setLoading(false);
        }
    }, [task, period]);

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <div>
                    <div>
                        <h4>{st('Summary')}:</h4>
                        <div>
                            <label className="mr5">{st('Metric')}:</label>
                            {metric?.full_Name || text.loading}
                        </div>
                        <div>
                            <label className="mr5">{st('Target')}:</label>
                            <Copy value={targetText} />
                        </div>
                    </div>
                    <div
                        className="chart-container mt10"
                        id={`chart-${task.id}`}
                        style={{ border: 'solid 1px #ebebeb', borderRadius: '3px', height: `400px` }}
                    >
                        <Chart
                            key={`chart-${task.id}-${goal?.id || '0'}`}
                            data={data}
                            id={`chart-${task.id}`}
                            keys={keys}
                            metric={metric}
                            format={metric?.format || 'Q'}
                            task={task}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MetricGraph;
